import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { Link, NavLink } from '@remix-run/react';
import { GQLCategoryFragment } from '@theprintbar/graphql';
import { WebRoutes, cn } from '@theprintbar/shared';
import { ProductQueryKeys, ProductSearchQuery, getProductSearchURL } from './ProductFilter/productSearch';

export interface CategoryListProps {
	category?: GQLCategoryFragment | null;
	categories?: GQLCategoryFragment[] | null;
	params: ProductSearchQuery;
	store?: any;
}

export const CategoryList = function ({ category, categories, params, store }: CategoryListProps) {
	if (!categories) return null;

	const sortedCategories = categories.sort((a, b) => a.order - b.order);

	const activeCategoryCode = category?.code || params?.category?.[params.category.length - 1];

	const parents = sortedCategories.filter(cat => !cat.parents?.length);

	return (
		<div>
			<div className="flex justify-between items-end h-16 border-b border-blue mb-4 pb-4">
				<h5 className="font-display font-light text-xl">Categories</h5>
				{!!category && (
					<Link
						to={store ? store.id + WebRoutes.Products(params) : WebRoutes.Products(params)}
						className="flex items-center gap-2 text-sm"
					>
						All Products
						<ArrowUpRightIcon className="w-3 h-3" />
					</Link>
				)}
			</div>
			<div className="pl-5">
				<ul>
					{parents.map(({ name, code, slug }) => {
						const active = activeCategoryCode === code;
						const children = categories.filter(c => c.parents?.some(p => p.code === code));
						const activeChild = children?.some(child => activeCategoryCode === child.code);
						const activeGrandChild = children?.some(child =>
							child.children?.some(grandChild => activeCategoryCode === grandChild.code),
						);
						const expanded = active || activeChild || activeGrandChild;

						return (
							<li className={cn('py-1', expanded && `negative`)} key={name}>
								<NavLink
									end
									className={cn(active ? 'active underline' : 'group group-hover:underline')}
									to={
										store?.id
											? getProductSearchURL(ProductQueryKeys.Category, code, params)
											: WebRoutes.Collection(slug, params)
									}
								>
									{name}
								</NavLink>
								{expanded && children?.length ? (
									<ul className="pl-4">
										{children.map(child => {
											const childActive = activeCategoryCode === child.code;
											const grandChildren = categories.filter(c => c.parents?.some(p => p.code === child.code));
											const grandChildActive = grandChildren?.some(
												grandChild => activeCategoryCode === grandChild.code,
											);
											const childExpanded = childActive || grandChildActive;
											return (
												<li
													className={cn('py-1', childExpanded && `negative`, !grandChildren?.length && `blank`)}
													key={child.name}
												>
													<NavLink
														end
														className={cn('group', childActive ? 'active underline' : 'group-hover:underline')}
														to={
															store?.id
																? getProductSearchURL(ProductQueryKeys.Category, child.code, params)
																: WebRoutes.Collection(child.slug, params)
														}
													>
														{child.name}
													</NavLink>
													{child.children?.length ? (
														<ul className="pl-4">
															{childExpanded &&
																child.children?.map(grandChild => {
																	const isActive = activeCategoryCode === grandChild.code;
																	const cat = categories.find(c => c.code === grandChild.code);
																	return cat ? (
																		<li
																			className={cn(`py-1 blank group-hover:underline`)}
																			key={grandChild.name}
																		>
																			<NavLink
																				end
																				className={cn(
																					isActive ? 'underline active cursor-default' : 'group',
																				)}
																				to={
																					store?.id
																						? getProductSearchURL(
																								ProductQueryKeys.Category,
																								cat.code,
																								params,
																						  )
																						: WebRoutes.Collection(cat.slug, params)
																				}
																			>
																				{grandChild.name}
																			</NavLink>
																		</li>
																	) : null;
																})}
														</ul>
													) : null}
												</li>
											);
										})}
									</ul>
								) : null}
							</li>
						);
					})}
				</ul>
			</div>
		</div>
	);
};
